import { ICoreContext } from '@msdyn365-commerce/core';
import { Cart, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

interface ITrackingContext {
    request: {
        cookies: Pick<ICoreContext['request']['cookies'], 'get' | 'isConsentGiven'>;
    };
    telemetry: Pick<ICoreContext['telemetry'], 'error'>;
}
type TrackingEvent = 'add_to_cart' | 'checkout_started' | 'purchase';

const pendingEvents = new Set<string>();
const attemptedEvents = new Set<string>();
let nextEventId = 0;

const hasAnalyticsConsent = (context: ITrackingContext): boolean => {
    const cookies = context.request.cookies;
    const consent = cookies.get('cookieConsent')?.value;
    return (
        cookies.isConsentGiven() &&
        Array.isArray(consent) &&
        consent.some(choice => choice.name === 'Analytics' && (choice.value === true || choice.value === 'true'))
    );
};

/**
 * Uses the installed THG API without requiring chatbot attribution or interaction.
 * Only consented events wait for a late SDK; pre-consent events are not replayed.
 */
export const trackTHGEvent = (
    context: ITrackingContext,
    event: TrackingEvent,
    data: Record<string, unknown>,
    deduplicationKey?: string
): void => {
    if (typeof window === 'undefined' || !hasAnalyticsConsent(context)) {
        return;
    }

    const key = `${event}:${deduplicationKey ?? ++nextEventId}`;
    if (pendingEvents.has(key) || attemptedEvents.has(key)) {
        return;
    }

    const storageKey = `dobbies:thg:${key}`;
    if (event === 'purchase') {
        try {
            if (window.sessionStorage.getItem(storageKey)) {
                return;
            }
        } catch (error) {
            context.telemetry.error(`THG purchase deduplication storage unavailable: ${error}`);
        }
    }

    pendingEvents.add(key);
    const deadline = Date.now() + 30000;
    const dispatch = (): void => {
        if (!hasAnalyticsConsent(context)) {
            pendingEvents.delete(key);
            return;
        }

        const chatbot = window.THGChatbot;
        if (typeof chatbot?.trackSiteEvent !== 'function') {
            if (Date.now() < deadline) {
                window.setTimeout(dispatch, 250);
            } else {
                pendingEvents.delete(key);
                context.telemetry.error(`THG ${event} not sent: trackSiteEvent unavailable after 30 seconds.`);
            }
            return;
        }

        pendingEvents.delete(key);
        // Do not retry an SDK call that throws: it may already have submitted the event.
        if (deduplicationKey) {
            attemptedEvents.add(key);
        }
        try {
            chatbot.trackSiteEvent(event, data);
        } catch (error) {
            context.telemetry.error(`THG ${event} SDK call failed: ${error}`);
            return;
        }

        if (event === 'purchase') {
            try {
                window.sessionStorage.setItem(storageKey, '1');
            } catch (error) {
                context.telemetry.error(`THG purchase deduplication could not be persisted: ${error}`);
            }
        }
    };
    dispatch();
};

export const trackTHGAddToCart = (context: ITrackingContext, product: SimpleProduct, quantity: number = 1): void => {
    const data: Record<string, unknown> = {
        value: product.Price,
        currency: 'GBP'
    };
    if (quantity > 1) {
        data.item_count = quantity;
    } else {
        data.product_sku = product.RecordId;
    }
    trackTHGEvent(context, 'add_to_cart', data);
};

export const trackTHGCheckout = (context: ITrackingContext, cart: Cart | undefined, isEditorialMode: boolean): void => {
    if (!isEditorialMode && cart?.Id && cart.CartLines?.length) {
        trackTHGEvent(
            context,
            'checkout_started',
            {
                item_skus: cart.CartLines.map(line => line.ProductId),
                value: cart.TotalAmount,
                currency: 'GBP'
            },
            cart.Id
        );
    }
};
